import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { type ReactElement } from 'react';

import { Alert, AlertDescription } from '@/ui/base/alert';

/**
 * O aviso curto de sucesso e de falha de uma ação de página (salvar, decidir,
 * aprovar, rejeitar, importar). É o único componente do tipo: as telas de
 * administração, Operação e Financeiro importam daqui, e nenhum módulo mantém
 * cópia própria (CLAUDE.md, "Regras de interface", item 1).
 *
 * POR QUE ISTO É UM ELEMENTO DA PÁGINA, E NÃO UM TOAST. O `<Toaster />` do
 * `sonner` está montado em `App.tsx`, e o toast serve para o aviso que passa
 * sozinho. Este aviso é outro: fica fixo junto da ação que o provocou e só
 * some quando a pessoa o fecha (`aoFechar`) ou a página o substitui. Quem
 * audita uma captura de tela precisa encontrar o resultado da ação onde ela foi
 * feita — por isso é um elemento de verdade no corpo da página, que a página
 * decide onde encaixar, em região viva. NÃO troque isto por toast.
 */

export type TomDaNotificacao = 'sucesso' | 'falha';

/** O que uma tela guarda no estado para mostrar (ou não) a `Notificacao`. */
export interface Aviso {
  tom: TomDaNotificacao;
  mensagem: string;
}

export interface NotificacaoProps {
  tom: TomDaNotificacao;
  /** Frase curta, em português, dizendo o que aconteceu ou o que fazer. */
  mensagem: string;
  /** Quando presente, mostra um botão que fecha o aviso. */
  aoFechar?: () => void;
}

export function Notificacao({ tom, mensagem, aoFechar }: NotificacaoProps): ReactElement {
  const sucesso = tom === 'sucesso';

  return (
    // O `Alert` do design system já fixa `role="alert"`. Um segundo `role` no
    // mesmo nó confundiria o leitor de tela sobre qual anunciar — por isso a
    // região viva fica neste `div` por fora, com `aria-live="polite"` para
    // anunciar sem roubar o foco de quem está digitando ou navegando.
    <div role="status" aria-live="polite">
      <Alert
        className={
          sucesso
            ? 'border-success/50 bg-success/10 text-success'
            : 'border-destructive/50 bg-destructive/10'
        }
      >
        {sucesso ? (
          <CheckCircle2 className="text-success" aria-hidden />
        ) : (
          <AlertTriangle className="text-destructive" aria-hidden />
        )}
        <AlertDescription className={sucesso ? 'text-success' : 'text-destructive'}>
          <div className="flex items-start justify-between gap-3">
            <span>{mensagem}</span>
            {aoFechar && (
              <button
                type="button"
                onClick={aoFechar}
                aria-label="Fechar aviso"
                className="focus-visible:ring-ring shrink-0 rounded-sm text-current opacity-70 hover:opacity-100 focus-visible:outline-hidden focus-visible:ring-2"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            )}
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

export default Notificacao;
